import { AutoAwesome, CopyAll, ThumbUp, ThumbDown } from "@mui/icons-material";
import { Card } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { intl } from "src/internationalization/intl";
import { Grid, Typography, IconButton, IconWrapper } from "src/lib/components/basics";
import { StyledMessage } from "./styled";
import { IChatMessageProps } from "./types";
import { setMessageFeedback } from "src/services/api/chat";
import { useState } from "react";
import { useSnackbarStore } from "src/store/snackbarStore";

const Message = (props: IChatMessageProps) => {
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

    const handleCopy = async () => {
        const textToCopy = props.children?.toString() || "";
        try {
            await navigator.clipboard.writeText(textToCopy);
        } catch (err) {
            console.error("Erro ao copiar texto:", err);
        }
    };

    const handleFeedback = async (liked: boolean) => {
        // Só permite feedback em mensagens de resposta que tenham jsonMessage e questionText
        if (!props.answer || !props.jsonMessage || !props.questionText) {
            return;
        }

        try {
            setIsSubmittingFeedback(true);
            await setMessageFeedback({
                answer: props.jsonMessage.natural_language_response || props.children?.toString() || "",
                question: props.questionText,
                cypher_query: props.jsonMessage.cypher_query || "",
                liked: liked ? "true" : "false"
            });
            showSnackbar(
                liked ? "Feedback positivo enviado com sucesso!" : "Feedback negativo enviado com sucesso!",
                "success"
            );
        } catch (error) {
            console.error("Erro ao enviar feedback:", error);
            showSnackbar(
                "Erro ao enviar feedback. Tente novamente.",
                "error"
            );
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    return (
        <StyledMessage container answer={props.answer} context={!!props.context}>
            <Grid item>
                {
                    !!props.context ?
                        <>
                            <Card>
                                <Grid container>
                                    <Grid>
                                        <IconWrapper color={"primary"}>
                                            <AutoAwesome />
                                        </IconWrapper>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h4" color="secondary">
                                            {intl('contextMessage')}:
                                        </Typography>
                                        <Typography variant="h4" color="secondary">
                                            {intl(props.context)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid>
                                    <Typography variant="body1">
                                        <Markdown
                                            skipHtml={false}
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                p: ({ ...childProps }) => <div {...childProps} />,
                                                code: ({ ...childProps }) => <span {...childProps} />,
                                                pre: ({ ...childProps }) => <span {...childProps} />,
                                                table: ({ children, ...rest }) => <table {...rest}>{children}</table>,
                                                thead: ({ children, ...rest }) => <thead {...rest}>{children}</thead>,
                                                tbody: ({ children, ...rest }) => <tbody {...rest}>{children}</tbody>,
                                                tr: ({ children, ...rest }) => <tr {...rest}>{children}</tr>,
                                                th: ({ children, ...rest }) => <th {...rest}>{children}</th>,
                                                td: ({ children, ...rest }) => <td {...rest}>{children}</td>
                                            }}
                                        >
                                            {props.children?.toString()}
                                        </Markdown>
                                    </Typography>
                                    <Typography variant="caption">
                                        {props.timestamp}
                                    </Typography>
                                </Grid>
                            </Card>
                            <Grid container justifyContent="flex-end" gap={1} sx={{ mt: 1 }}>
                                {props.answer && props.jsonMessage && props.questionText && (
                                    <>
                                        <IconButton 
                                            onClick={() => handleFeedback(true)} 
                                            variant="filled" 
                                            color="primary" 
                                            size="small"
                                            disabled={isSubmittingFeedback}
                                        >
                                            <ThumbUp />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleFeedback(false)} 
                                            variant="filled" 
                                            color="primary" 
                                            size="small"
                                            disabled={isSubmittingFeedback}
                                        >
                                            <ThumbDown />
                                        </IconButton>
                                    </>
                                )}
                                <IconButton onClick={handleCopy} variant="filled" color="primary" size="small">
                                    <CopyAll />
                                </IconButton>
                            </Grid>
                        </>
                        :
                        <>
                            <Card>
                                <Typography variant="body1">
                                        <Markdown
                                            skipHtml={false}
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                p: ({ ...childProps }) => <div {...childProps} />,
                                                code: ({ ...childProps }) => <span {...childProps} />,
                                                pre: ({ ...childProps }) => <span {...childProps} />,
                                                table: ({ children, ...rest }) => <table {...rest}>{children}</table>,
                                                thead: ({ children, ...rest }) => <thead {...rest}>{children}</thead>,
                                                tbody: ({ children, ...rest }) => <tbody {...rest}>{children}</tbody>,
                                                tr: ({ children, ...rest }) => <tr {...rest}>{children}</tr>,
                                                th: ({ children, ...rest }) => <th {...rest}>{children}</th>,
                                                td: ({ children, ...rest }) => <td {...rest}>{children}</td>
                                            }}
                                        >
                                            {props.children?.toString()}
                                        </Markdown>
                                    </Typography>
                                <Typography variant="caption">
                                    {props.timestamp}
                                </Typography>
                            </Card>
                            <Grid container justifyContent="flex-end" gap={1} sx={{ mt: 1 }}>
                                {props.answer && props.jsonMessage && props.questionText && (
                                    <>
                                        <IconButton 
                                            onClick={() => handleFeedback(true)} 
                                            variant="filled" 
                                            color="primary" 
                                            size="small"
                                            disabled={isSubmittingFeedback}
                                        >
                                            <ThumbUp />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleFeedback(false)} 
                                            variant="filled" 
                                            color="primary" 
                                            size="small"
                                            disabled={isSubmittingFeedback}
                                        >
                                            <ThumbDown />
                                        </IconButton>
                                    </>
                                )}
                                <IconButton onClick={handleCopy} variant="filled" color="primary" size="small">
                                    <CopyAll />
                                </IconButton>
                            </Grid>
                        </>
                }
            </Grid>
        </StyledMessage>
    );
};

export { Message };
